import { startCase, get, isEmpty, isArray } from "lodash";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Chip, Stack, Box } from "@mui/material";

import { TableColumn } from "..";

import { generateFullAddress } from "@/helpers/address";
import { dayjsUTC } from "@/helpers/api";

export function formatRecord<TRecord>(
  record: TRecord,
  field: TableColumn<TRecord>["field"],
  format: TableColumn<TRecord>["format"],
  formatOptions: TableColumn<TRecord>["formatOptions"]
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fieldData: any = get(record, field as string, "");

  if (fieldData === "11/11/2600") return "Present";

  switch (format) {
    case "start-case":
      return startCase(fieldData as string);

    case "boolean":
      if (fieldData === true) {
        return "Yes";
      }

      if (fieldData === false) {
        return "No";
      }

      return "";

    case "dollar":
      if (fieldData === null || fieldData === undefined) return "N/A";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(fieldData as number);

    case "currency":
      if (!fieldData || fieldData.amount === null || fieldData.amount === undefined) return "N/A";

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: fieldData.currency_code ?? "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(fieldData.amount);

    case "number":
      return new Intl.NumberFormat("en-US", {}).format(fieldData);

    case "percent":
      if (fieldData) return `${fieldData}%`;
      return null;

    case "address":
      return generateFullAddress(fieldData);

    case "date": {
      const dateFormat = formatOptions?.dateFormat ?? "MM/DD/YYYY";
      if (!fieldData || typeof fieldData === "number") return fieldData;
      if (field === "to_date" && (record as { is_current: boolean }).is_current) return "Current";
      const formattedDate = dayjsUTC(fieldData, dateFormat);
      if (formattedDate === dayjsUTC("11/11/2600", dateFormat) || formattedDate === dayjsUTC("11/10/2600", dateFormat))
        return "Present";
      return formattedDate;
    }

    case "chip": {
      return <Chip size="small" label={fieldData} />;
    }

    case "chips":
      if (isEmpty(fieldData)) {
        return "";
      } else if (Array.isArray(fieldData)) {
        return (
          <Stack direction="row" maxWidth="400px" spacing={1}>
            {fieldData.map((label) => (
              <Chip key={label} size="small" label={startCase(label)} />
            ))}
          </Stack>
        );
      } else {
        return <Chip size="small" label={startCase(fieldData)} />;
      }

    case "list":
      if (!isArray(fieldData)) {
        return "";
      }
      return fieldData.join(", ").toString();

    case "attachment":
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <AttachFileIcon />
          <Box>{(record as { file_count: number }).file_count}</Box>
        </Stack>
      );

    case "party":
      if (fieldData.type === "person") {
        return fieldData?.bd_person?.full_name;
      } else if (fieldData.type === "business") {
        return fieldData?.bd_business_entity?.business_name;
      } else {
        return "";
      }

    default:
      return fieldData;
  }
}
